// useCoolingSla — the active tenant's cooling SLA thresholds (FCX-22),
// resolved PER CHANNEL (FCX-26 M2b).
//
// A packhouse running two force-cooling channels needs different numbers per
// lane — strawberries are far more perishable than blueberries — so
// packhouse_config.cooling_sla_h carries two shapes, told apart by VALUE TYPE
// (never by name, so a flow key can never collide with a stage key):
//
//   flat   {"COOLING":[4,8]}                  a STAGE entry (array) → every channel
//   nested {"STRAWBERRY":{"COOLING":[2,3]}}   a CHANNEL entry (object) → that lane
//
// Resolution is channel → flat all-channel default → the in-code STAGE_SLA_H,
// mirroring mig 20260716090000's check_cooling_slas() exactly so the screen and
// the bell can never disagree about what "too long" means.
//
// The hook returns a RESOLVER, not a record: a board or register holds batches
// from several channels at once, and one record per render could only ever be
// right for one of them. Callers pass each row's own lane —
// `slaFor(channelOf(b))` — with `useEntityFlowResolver()` supplying the lane.
//
// The recommendations-hub evaluator stays on the in-code defaults (its fetch
// runs outside React) — a documented, deliberate exception.

import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { STAGE_SLA_H, type CoolingStage } from '@/lib/coolingSla'

export type CoolingSlaRecord = Record<CoolingStage, [number, number]>

/** Thresholds for ONE lane. `flowKey` null → the flat/default chain only. */
export type CoolingSlaResolver = (flowKey: string | null | undefined) => CoolingSlaRecord

// A CHANNEL entry: an object of stages. Arrays are stage bands, not channels.
const asChannel = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null

// A STAGE band: exactly [warn, crit], both positive. Anything else falls back
// silently — these are display thresholds, not a boundary.
const asBand = (v: unknown): [number, number] | null =>
  Array.isArray(v) && v.length === 2 && v.every(n => typeof n === 'number' && n > 0)
    ? [v[0] as number, v[1] as number]
    : null

/**
 * Resolve a config blob to one lane's thresholds: channel override → flat
 * all-channel default → in-code default, per stage. Pure, so client and DB
 * can be tested against the same table of cases.
 */
export function resolveCoolingSla(config: unknown, flowKey: string | null | undefined): CoolingSlaRecord {
  const root    = asChannel(config)
  const channel = flowKey ? asChannel(root?.[flowKey]) : null
  const out = { ...STAGE_SLA_H }
  for (const stage of Object.keys(out) as CoolingStage[]) {
    const band = asBand(channel?.[stage]) ?? asBand(root?.[stage])
    if (band) out[stage] = band
  }
  return out
}

export function useCoolingSla(): CoolingSlaResolver {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  const { data } = useQuery({
    queryKey: ['cooling-sla', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('packhouse_config')
        .select('cooling_sla_h')
        .eq('tenant_id', tenantId!)
        .maybeSingle()
      if (error) throw error
      return (data?.cooling_sla_h ?? null) as unknown
    },
  })
  // Stable while the config is: callers put this straight into useMemo deps.
  return useCallback((flowKey: string | null | undefined) => resolveCoolingSla(data, flowKey), [data])
}
